/**
 * TrapTestScenarios - Предустановленные сценарии для тестирования механики ловушек
 *
 * Использует существующую игровую логику (GameOrchestrator, TrapResolver, MoveExecutor)
 * Не создаёт новую логику - только конфигурации для тестирования
 */
import { Position } from '../domain/Position';
import { TrapSituationType } from '../mechanics/trap/TrapResolver';
/**
 * Создаёт позицию из координат
 */
function pos(x, y) {
    return new Position(x, y);
}
/**
 * Создаёт территорию для сценария
 */
function territory(x, y, owner, occupied = true) {
    return { position: pos(x, y), owner, occupied };
}
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 1: MONOPOLY (Single Color Trap)
 * ═══════════════════════════════════════════════════════════════
 *
 * Все 4 ортогональных соседа принадлежат одной стороне
 * Результат: мгновенный захват этой стороной
 *
 *       [1]
 *    [1][X][1]
 *       [1]
 */
export const SCENARIO_MONOPOLY = {
    id: 'monopoly',
    name: 'Monopoly Trap (Single Color)',
    description: 'Все 4 ортогональных соседа одного цвета - мгновенный захват',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Игрок ходит конём с (7,6)
    targetPosition: pos(5, 5), // В ловушку на (5,5) - ход конём (-2, -1)
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0), // Стартовая позиция
        // Окружение (Side 1) - все 4 ортогональных соседа
        territory(5, 4, 1), // Сверху
        territory(5, 6, 1), // Снизу
        territory(4, 5, 1), // Слева
        territory(6, 5, 1), // Справа
        // Дополнительные территории Side 1 для силы
        territory(4, 4, 1),
        territory(6, 4, 1),
        territory(4, 6, 1),
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 2: FORCE-BASED (Multi-color, no escape)
 * ═══════════════════════════════════════════════════════════════
 *
 * Несколько сторон окружают, нет возможности сбежать
 * Побеждает сторона с наибольшей силой
 *
 *       [1]
 *    [2][X][1]
 *       [2]
 *
 * Side 1: orth=2*3=6, diag=0
 * Side 2: orth=2*3=6, diag=0
 * При равенстве - смотрим диагонали, потом global
 */
export const SCENARIO_FORCE_BASED = {
    id: 'force_based',
    name: 'Force-Based Trap',
    description: 'Несколько сторон окружают, побеждает сильнейший по формуле',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'captured', by: 1 }, // Side 1 имеет больше диагоналей
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Ход конём (-2, -1) -> (5,5)
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение Side 1 (верх и право)
        territory(5, 4, 1),
        territory(6, 5, 1),
        // Окружение Side 2 (низ и лево)
        territory(5, 6, 2),
        territory(4, 5, 2),
        // Диагонали для Side 1 (дают преимущество)
        territory(4, 4, 1),
        territory(6, 4, 1),
        // Диагонали для Side 2
        territory(4, 6, 2),
    ],
    movesInRound: 2, // Последний ход в раунде - нельзя сбежать
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 3: ESCAPABLE (Can escape)
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок окружён, но есть свободная позиция для хода конём
 * Результат: отложенное разрешение, игрок может попробовать сбежать
 *
 *       [1]      [ ]  <- свободно для побега
 *    [2][X][1]
 *       [2]
 */
export const SCENARIO_ESCAPABLE = {
    id: 'escapable',
    name: 'Escapable Trap',
    description: 'Игрок может сбежать - есть свободная позиция коня',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Ход конём (-2, -1) -> (5,5)
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение (но позиция (3, 4) или (7, 4) свободна для хода конём)
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 2),
        territory(4, 5, 2),
        // Диагонали
        territory(4, 4, 1),
        territory(6, 6, 2),
    ],
    movesInRound: 0, // Есть ходы в запасе
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 4: PARITY (Equal forces - BURN)
 * ═══════════════════════════════════════════════════════════════
 *
 * Силы равны на всех уровнях - территория сгорает
 *
 *       [1]
 *    [1][X][2]
 *       [2]
 *
 * Side 1: orth=2*3=6
 * Side 2: orth=2*3=6
 * Диагонали тоже равны -> BURNED
 */
export const SCENARIO_PARITY = {
    id: 'parity',
    name: 'Parity Trap (BURN)',
    description: 'Силы равны - территория сгорает',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'burned' },
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Ход конём (-2, -1) -> (5,5)
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение Side 1 (верх и лево)
        territory(5, 4, 1),
        territory(4, 5, 1),
        // Окружение Side 2 (низ и право)
        territory(5, 6, 2),
        territory(6, 5, 2),
        // Диагонали равные
        territory(4, 4, 1),
        territory(6, 6, 2),
        territory(6, 4, 2),
        territory(4, 6, 1),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 5: ESCAPE SUCCESS
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок попадает в escapable ловушку и успешно сбегает
 * Требует 2 хода: 1) в ловушку, 2) побег
 */
export const SCENARIO_ESCAPE_SUCCESS = {
    id: 'escape_success',
    name: 'Escape Success',
    description: 'Игрок сбегает из escapable ловушки',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'escaped' },
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Ход конём (-2, -1) -> (5,5)
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение - но (3, 4) свободна для побега конём
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 2),
        territory(4, 5, 2),
    ],
    movesInRound: 0, // 3 хода в запасе
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ 6: ESCAPE FAILURE
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок попадает в escapable ловушку, но не успевает сбежать
 * (заканчиваются ходы в раунде)
 */
export const SCENARIO_ESCAPE_FAILURE = {
    id: 'escape_failure',
    name: 'Escape Failure',
    description: 'Игрок не успевает сбежать - ловушка разрешается',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'captured', by: 1 }, // После конца раунда
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Ход конём (-2, -1) -> (5,5)
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 2),
        territory(4, 5, 2),
        // Диагонали для Side 1
        territory(4, 4, 1),
        territory(6, 4, 1),
    ],
    movesInRound: 1, // Только 2 хода осталось - в ловушку и ещё 1
    round: 1,
};
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                    КАТЕГОРИЯ A: PENDING TRAPS                            ║
// ╚══════════════════════════════════════════════════════════════════════════╝
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ A1: MULTIPLE PENDING TRAPS
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок входит в 2 ловушки за один раунд
 * Требует: загрузить, ходить в первую ловушку, затем во вторую
 *
 *    [1]       [2]
 * [1][X1][1] [2][X2][2]
 *    [1]       [2]
 */
export const SCENARIO_MULTIPLE_PENDING = {
    id: 'multiple_pending',
    name: 'A1: Multiple Pending Traps',
    description: 'Игрок входит в 2 escapable ловушки за 1 раунд - все должны трекаться',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 1 },
    playerSide: 0,
    playerStartPosition: pos(3, 5),
    targetPosition: pos(5, 4), // Первая ловушка
    territories: [
        // Игрок (Side 0)
        territory(3, 5, 0),
        // Первая ловушка на (5, 4)
        territory(5, 3, 1),
        territory(5, 5, 1),
        territory(4, 4, 1),
        territory(6, 4, 1),
        // Вторая ловушка на (9, 4) - игрок может попасть туда следующим ходом конём
        territory(9, 3, 2),
        territory(9, 5, 2),
        territory(8, 4, 2),
        territory(10, 4, 2),
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ A2: ESCAPE BY BREAKING SURROUNDING
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок в ловушке (полное окружение), делает ход который захватывает
 * территорию из окружения - разрывает кольцо.
 * Результат: ловушка автоматически разрешается как "побег"
 *
 *       [1]
 *    [2][X][1]   <- Side 2 владеет (4,5), игрок может захватить
 *       [2]
 *
 * Алгоритм теста:
 * 1. executeScenarioMove() - ход на (5,5), попадаем в ESCAPABLE ловушку
 * 2. executeFollowupMove("(5,5)", "(4,3)") - ход конём захватывает (4,3)
 * 3. executeFollowupMove("(4,3)", "(4,5)") - захватываем (4,5) у Side 2
 *    ТЕПЕРЬ окружение разорвано - один из ортогональных соседей = свой
 */
export const SCENARIO_ESCAPE_BY_CAPTURE = {
    id: 'escape_by_capture',
    name: 'A2: Escape by Breaking Surrounding',
    description: 'Игрок разрывает окружение захватом соседней территории',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'escaped' },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // ПОЛНОЕ окружение - ВСЕ 4 ортогональных соседа враждебные
        territory(5, 4, 1), // сверху - Side 1
        territory(6, 5, 1), // справа - Side 1
        territory(4, 5, 2), // слева - Side 2 (можно захватить для побега)
        territory(5, 6, 2), // снизу - Side 2
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ A3: ROUND BOUNDARY RESOLUTION
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок делает ровно 3 хода, последний - в ловушку
 * Ловушка должна разрешиться в конце раунда
 */
export const SCENARIO_ROUND_BOUNDARY = {
    id: 'round_boundary',
    name: 'A3: Round Boundary Resolution',
    description: 'Последний ход раунда в ловушку - разрешается сразу',
    expectedTrapType: TrapSituationType.FORCE_BASED, // Нет ходов = force-based
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Окружение Side 1
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 1),
        territory(4, 5, 1),
        // Диагонали для Side 1
        territory(4, 4, 1),
        territory(6, 4, 1),
    ],
    movesInRound: 2, // Это будет 3-й ход = последний
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ A4: LEAVE WITHOUT ESCAPING
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок уходит с позиции ловушки, не разрывая окружение
 * Территория должна передаться выбранной стороне
 */
export const SCENARIO_LEAVE_NO_ESCAPE = {
    id: 'leave_no_escape',
    name: 'A4: Leave Without Escaping',
    description: 'Игрок уходит из ловушки не разорвав окружение - территория передаётся',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок (Side 0)
        territory(7, 6, 0),
        // Полное окружение
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 2),
        territory(4, 5, 2),
        // Диагонали для Side 1 (больше силы)
        territory(4, 4, 1),
        territory(6, 4, 1),
        territory(6, 6, 1),
    ],
    movesInRound: 0,
    round: 1,
};
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                  КАТЕГОРИЯ B: FORCE CALCULATION                          ║
// ╚══════════════════════════════════════════════════════════════════════════╝
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ B1: ORTHOGONAL VS DIAGONAL
 * ═══════════════════════════════════════════════════════════════
 *
 * Side 1: 2 ортогональных = 6
 * Side 2: 1 ортогональная + 4 диагонали = 3 + 4 = 7
 * Side 3: 1 ортогональная = 3
 * Результат: Side 1 побеждает — ортогонали сравниваются первыми (6 > 3), хотя
 * взвешенная сумма у стороны 2 больше (7 > 6).
 *
 *   [2][1][2]
 *   [2][X][1]
 *   [2][3][2]
 */
export const SCENARIO_ORTH_VS_DIAG = {
    id: 'orth_vs_diag',
    name: 'B1: Orthogonal vs Diagonal',
    description: 'Ортогональные (×3) важнее диагональных (×1)',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Side 1: 2 ортогональных
        territory(5, 4, 1), // top
        territory(6, 5, 1), // right
        // Side 2: 1 ортогональная
        territory(4, 5, 2), // left
        // Side 3: 1 ортогональная
        territory(5, 6, 3), // bottom
        // Side 2: все 4 диагонали (но весят меньше)
        territory(4, 4, 2),
        territory(6, 4, 2),
        territory(4, 6, 2),
        territory(6, 6, 2),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ B2: GLOBAL FORCE TIEBREAKER
 * ═══════════════════════════════════════════════════════════════
 *
 * Ортогональные и диагональные равны, но Side 1 имеет больше территорий глобально
 */
export const SCENARIO_GLOBAL_TIEBREAKER = {
    id: 'global_tiebreaker',
    name: 'B2: Global Force Tiebreaker',
    description: 'Глобальные силы решают при паритете орт/диаг',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение - паритет
        territory(5, 4, 1), // top - Side 1
        territory(6, 5, 2), // right - Side 2
        territory(5, 6, 1), // bottom - Side 1
        territory(4, 5, 2), // left - Side 2
        // Диагонали - паритет
        territory(4, 4, 1),
        territory(6, 6, 1),
        territory(6, 4, 2),
        territory(4, 6, 2),
        // Глобальные территории Side 1 (больше)
        territory(9, 9, 1),
        territory(9, 10, 1),
        territory(10, 9, 1),
        territory(10, 10, 1),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ B3: BURNED TERRITORY (Full Parity)
 * ═══════════════════════════════════════════════════════════════
 *
 * Полный паритет на всех уровнях → территория сгорает
 */
export const SCENARIO_BURNED_FULL_PARITY = {
    id: 'burned_full_parity',
    name: 'B3: Burned Territory (Full Parity)',
    description: 'Полный паритет: орт, диаг, глобал → территория сгорает',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'burned' },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение - идеальный паритет
        territory(5, 4, 1), // top
        territory(6, 5, 2), // right
        territory(5, 6, 2), // bottom
        territory(4, 5, 1), // left
        // Диагонали - паритет
        territory(4, 4, 1),
        territory(6, 6, 1),
        territory(6, 4, 2),
        territory(4, 6, 2),
        // Глобальные - паритет (по 2 дополнительных каждому)
        territory(9, 9, 1),
        territory(9, 10, 1),
        territory(10, 9, 2),
        territory(10, 10, 2),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ B4: SELF-SURROUNDING
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок окружён своими же территориями
 * По правилам - это тоже ловушка!
 */
export const SCENARIO_SELF_SURROUNDING = {
    id: 'self_surrounding',
    name: 'B4: Self-Surrounding',
    description: 'Игрок окружён своими территориями - тоже ловушка',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 0 }, // Сам себе
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение своими территориями
        territory(5, 4, 0),
        territory(6, 5, 0),
        territory(5, 6, 0),
        territory(4, 5, 0),
    ],
    movesInRound: 2,
    round: 1,
};
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                    КАТЕГОРИЯ C: EDGE CASES                               ║
// ╚══════════════════════════════════════════════════════════════════════════╝
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ C1: CORNER TRAP
 * ═══════════════════════════════════════════════════════════════
 *
 * Ловушка в углу: 2 края + 2 врага = полное окружение
 *
 * [Край][Край]
 * [Край][X][1]
 *       [1]
 */
export const SCENARIO_CORNER_TRAP = {
    id: 'corner_trap',
    name: 'C1: Corner Trap',
    description: 'Ловушка в углу (0,0): 2 края + 2 врага',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(2, 1), // Ход конём к (0, 0)
    targetPosition: pos(0, 0),
    territories: [
        // Игрок
        territory(2, 1, 0),
        // Окружение (только 2 клетки нужны, остальное - края)
        territory(1, 0, 1), // справа
        territory(0, 1, 1), // снизу
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ C2: EDGE TRAP
 * ═══════════════════════════════════════════════════════════════
 *
 * Ловушка у края: 1 край + 3 врага
 *
 * [Край]
 * [1][X][1]
 *    [1]
 */
export const SCENARIO_EDGE_TRAP = {
    id: 'edge_trap',
    name: 'C2: Edge Trap',
    description: 'Ловушка у края: 1 край + 3 врага',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 1), // Ход конём к (5, 0)
    targetPosition: pos(5, 0),
    territories: [
        // Игрок
        territory(7, 1, 0),
        // Окружение (3 клетки + 1 край сверху)
        territory(4, 0, 1), // слева
        territory(6, 0, 1), // справа
        territory(5, 1, 1), // снизу
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ C3: OPPOSITE CORNER
 * ═══════════════════════════════════════════════════════════════
 *
 * Ловушка в противоположном углу (10, 10)
 */
export const SCENARIO_OPPOSITE_CORNER = {
    id: 'opposite_corner',
    name: 'C3: Opposite Corner Trap',
    description: 'Ловушка в углу (10,10): 2 края + 2 врага',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(8, 9), // Ход конём к (10, 10)
    targetPosition: pos(10, 10),
    territories: [
        // Игрок
        territory(8, 9, 0),
        // Окружение
        territory(9, 10, 1), // слева
        territory(10, 9, 1), // сверху
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ C4: DIAGONAL ONLY ENEMIES
 * ═══════════════════════════════════════════════════════════════
 *
 * Только диагональные соседи - враги, ортогональные - нейтральны
 * НЕ должна быть ловушкой!
 *
 *    [1]   [1]
 *    [N][X][N]
 *    [1]   [1]
 */
export const SCENARIO_DIAGONAL_ONLY = {
    id: 'diagonal_only',
    name: 'C4: Diagonal Only Enemies',
    description: 'Только диагональные враги - НЕ ловушка',
    expectedTrapType: 'none',
    expectedResult: { type: 'escaped' }, // Нет ловушки = обычный ход
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Только диагонали заняты
        territory(4, 4, 1),
        territory(6, 4, 1),
        territory(4, 6, 1),
        territory(6, 6, 1),
        // Ортогональные (5,4), (5,6), (4,5), (6,5) - нейтральны
    ],
    movesInRound: 0,
    round: 1,
};
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                   КАТЕГОРИЯ D: CHAIN REACTIONS                           ║
// ╚══════════════════════════════════════════════════════════════════════════╝
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ D1: TRAP → SURROUNDING CAPTURE
 * ═══════════════════════════════════════════════════════════════
 *
 * Передача территории завершает окружение другой группы
 */
export const SCENARIO_TRAP_CHAIN_CAPTURE = {
    id: 'trap_chain_capture',
    name: 'D1: Trap → Surrounding Capture',
    description: 'Передача территории запускает цепной захват окружением',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение ловушки - Side 1 победит
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 1),
        territory(4, 5, 1),
        // Территория Side 2, которая будет окружена после захвата (5,5) стороной 1
        territory(5, 3, 2, true), // Будет окружена когда (5,5) станет Side 1
        // Дополнительное окружение для (5, 3) после захвата
        territory(4, 3, 1),
        territory(6, 3, 1),
        territory(5, 2, 1),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ D2: TRAP → ELIMINATION
 * ═══════════════════════════════════════════════════════════════
 *
 * Сторона теряет последнюю территорию в ловушке
 */
export const SCENARIO_TRAP_ELIMINATION = {
    id: 'trap_elimination',
    name: 'D2: Trap → Elimination',
    description: 'Потеря последней территории в ловушке → элиминация',
    expectedTrapType: TrapSituationType.SINGLE_COLOR,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6), // Это ЕДИНСТВЕННАЯ территория Side 0
    targetPosition: pos(5, 5),
    territories: [
        // Игрок - только одна территория!
        territory(7, 6, 0),
        // Окружение Side 1
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 1),
        territory(4, 5, 1),
    ],
    movesInRound: 2,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ D3: TRAP → REANIMATION
 * ═══════════════════════════════════════════════════════════════
 *
 * Передача территории "мёртвой" стороне реанимирует её
 */
export const SCENARIO_TRAP_REANIMATION = {
    id: 'trap_reanimation',
    name: 'D3: Trap → Reanimation',
    description: 'Передача территории элиминированной стороне реанимирует её',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 3 }, // Выбираем мёртвую сторону 3
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение - Side 1 и Side 3
        // Side 3 будет элиминирована (без территорий кроме этой одной)
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 3), // Side 3 - единственная территория
        territory(4, 5, 3), // Side 3 - единственная территория (оставим 2 для реанимации)
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ D4: MULTI-STEP CHAIN
 * ═══════════════════════════════════════════════════════════════
 *
 * Цепочка: Ловушка → захват → ещё ловушка
 */
export const SCENARIO_MULTI_STEP_CHAIN = {
    id: 'multi_step_chain',
    name: 'D4: Multi-Step Chain',
    description: 'Цепная реакция: ловушка → захват → следующая ловушка',
    expectedTrapType: TrapSituationType.FORCE_BASED,
    expectedResult: { type: 'captured', by: 1 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Первая ловушка - Side 1 победит
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 1),
        territory(4, 5, 2),
        // После захвата (5,5) Side 1, территория Side 2 на (4, 5) станет окружена
        // Окружение для (4, 5)
        territory(3, 5, 1),
        territory(4, 4, 1),
        territory(4, 6, 1),
    ],
    movesInRound: 2,
    round: 1,
};
// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                    КАТЕГОРИЯ E: REANIMATION                              ║
// ╚══════════════════════════════════════════════════════════════════════════╝
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ E1: REANIMATION VIA TRAP CHOICE
 * ═══════════════════════════════════════════════════════════════
 *
 * Игрок выбирает элиминированную сторону как получателя территории
 * Эта сторона должна реанимироваться
 */
export const SCENARIO_REANIMATE_BY_CHOICE = {
    id: 'reanimate_by_choice',
    name: 'E1: Reanimation via Trap Choice',
    description: 'Выбор элиминированной стороны как получателя → реанимация',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 3 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Side 1 активна
        territory(5, 4, 1),
        territory(6, 5, 1),
        // Side 3 - имеет территории в окружении, но больше ничего
        territory(5, 6, 3),
        territory(4, 5, 3),
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ E2: REANIMATION ACTIVE FIELDS
 * ═══════════════════════════════════════════════════════════════
 *
 * После реанимации новая территория должна стать ходовым полем
 */
export const SCENARIO_REANIMATE_ACTIVE_FIELD = {
    id: 'reanimate_active_field',
    name: 'E2: Reanimation + Active Fields',
    description: 'Реанимированная сторона получает ходовое поле',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 4 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 4), // Side 4
        territory(4, 5, 4), // Side 4
    ],
    movesInRound: 0,
    round: 1,
};
/**
 * ═══════════════════════════════════════════════════════════════
 * СЦЕНАРИЙ E3: REANIMATION MID-ROUND
 * ═══════════════════════════════════════════════════════════════
 *
 * Реанимация в середине раунда - правильный порядок ходов
 */
export const SCENARIO_REANIMATE_MID_ROUND = {
    id: 'reanimate_mid_round',
    name: 'E3: Reanimation Mid-Round',
    description: 'Реанимация в середине раунда - проверка порядка ходов',
    expectedTrapType: TrapSituationType.ESCAPABLE,
    expectedResult: { type: 'deferred', chosenSide: 5 },
    playerSide: 0,
    playerStartPosition: pos(7, 6),
    targetPosition: pos(5, 5),
    territories: [
        // Игрок
        territory(7, 6, 0),
        // Окружение
        territory(5, 4, 1),
        territory(6, 5, 1),
        territory(5, 6, 5), // Side 5
        territory(4, 5, 5), // Side 5
    ],
    movesInRound: 0,
    round: 2, // Середина игры
};
/**
 * Все сценарии
 */
export const TRAP_TEST_SCENARIOS = {
    // Базовые сценарии
    monopoly: SCENARIO_MONOPOLY,
    force_based: SCENARIO_FORCE_BASED,
    escapable: SCENARIO_ESCAPABLE,
    parity: SCENARIO_PARITY,
    escape_success: SCENARIO_ESCAPE_SUCCESS,
    escape_failure: SCENARIO_ESCAPE_FAILURE,
    // Категория A: Pending Traps
    multiple_pending: SCENARIO_MULTIPLE_PENDING,
    escape_by_capture: SCENARIO_ESCAPE_BY_CAPTURE,
    round_boundary: SCENARIO_ROUND_BOUNDARY,
    leave_no_escape: SCENARIO_LEAVE_NO_ESCAPE,
    // Категория B: Force Calculation
    orth_vs_diag: SCENARIO_ORTH_VS_DIAG,
    global_tiebreaker: SCENARIO_GLOBAL_TIEBREAKER,
    burned_full_parity: SCENARIO_BURNED_FULL_PARITY,
    self_surrounding: SCENARIO_SELF_SURROUNDING,
    // Категория C: Edge Cases
    corner_trap: SCENARIO_CORNER_TRAP,
    edge_trap: SCENARIO_EDGE_TRAP,
    opposite_corner: SCENARIO_OPPOSITE_CORNER,
    diagonal_only: SCENARIO_DIAGONAL_ONLY,
    // Категория D: Chain Reactions
    trap_chain_capture: SCENARIO_TRAP_CHAIN_CAPTURE,
    trap_elimination: SCENARIO_TRAP_ELIMINATION,
    trap_reanimation: SCENARIO_TRAP_REANIMATION,
    multi_step_chain: SCENARIO_MULTI_STEP_CHAIN,
    // Категория E: Reanimation
    reanimate_by_choice: SCENARIO_REANIMATE_BY_CHOICE,
    reanimate_active_field: SCENARIO_REANIMATE_ACTIVE_FIELD,
    reanimate_mid_round: SCENARIO_REANIMATE_MID_ROUND,
};
/**
 * Список всех сценариев для UI
 */
export const SCENARIO_LIST = Object.values(TRAP_TEST_SCENARIOS);
/**
 * Загружает сценарий на доску
 * ВАЖНО: Ожидает свежую доску из new Board()
 */
export function loadScenarioToBoard(board, scenario) {
    // Активируем игрока и устанавливаем его стартовую позицию
    const playerSide = board.getSide(scenario.playerSide);
    if (playerSide) {
        playerSide.reanimate(scenario.playerStartPosition);
        // Территория игрока
        const playerTerritory = board.getTerritoryAt(scenario.playerStartPosition);
        if (playerTerritory) {
            playerTerritory.capture(scenario.playerSide);
            playerTerritory.occupy(scenario.playerSide);
        }
    }
    // Загружаем территории окружения из сценария
    for (const setup of scenario.territories) {
        // Пропускаем стартовую позицию игрока (уже обработана)
        if (setup.position.equals(scenario.playerStartPosition))
            continue;
        const territory = board.getTerritoryAt(setup.position);
        if (territory) {
            territory.capture(setup.owner);
            if (setup.occupied !== false) {
                territory.occupy(setup.owner);
            }
            const side = board.getSide(setup.owner);
            if (side) {
                // Активируем сторону если ещё не активна
                if (!side.isActive) {
                    side.reanimate(setup.position);
                }
                side.captureTerritory(setup.position);
                side.addTurnActiveField(setup.position);
            }
        }
    }
    // Устанавливаем текущую сторону
    board.restoreState({
        territories: board.territories,
        sides: board.sides,
        round: scenario.round ?? 1,
        currentSide: scenario.playerSide,
    });
    // Устанавливаем количество ходов
    if (playerSide && scenario.movesInRound) {
        for (let i = 0; i < scenario.movesInRound; i++) {
            playerSide.makeMove();
        }
    }
}
//# sourceMappingURL=TrapTestScenarios.js.map